import { ComponentFields, Field, Item } from '@sitecore-jss/sitecore-jss-nextjs';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';

import { DictionaryFields, SitecoreField, SitecoreFieldTypes } from '../models';
import { getSharedDictionary } from './get-shared-dictionary';
import { isBooleanField } from './is-boolean-field';
import { isDictionaryField } from './is-dictionary-field';
import { isDroptree } from './is-droptree';
import { isFilledIn } from './is-filled-in';
import { isLinkField } from './is-link-field';
import { isMediaItem } from './is-media-item';

const isItem = (value: Field | Item | Item[]): value is Item => {
  return 'fields' in value;
};

const isItemArray = (value: Field | Item | Item[] | unknown): value is Item[] => {
  const isArray = Array.isArray(value);
  if (!isArray) return false;
  if ((value as unknown[]).length === 0) return true;
  const [firstItem] = value as unknown[];
  return isItem(firstItem as Field | Item | Item[]);
};

const isJsonValueField = (
  field: Field | Item | Item[] | unknown
): field is Record<'jsonValue', Item[]> => {
  return (
    !!field &&
    typeof field === 'object' &&
    'jsonValue' in field &&
    isItemArray((field as Record<'jsonValue', unknown>).jsonValue)
  );
};

/**
 * Recursively maps Sitecore localized/shared/dictionary field variants to a flat fields object.
 * Resolves various Sitecore item patterns into a consistent shape.
 *
 * @param item - A Sitecore fields object (or ComponentFields).
 * @returns A flattened record with resolved field values.
 */
export const mapSharedPropertiesToFields = <ReturnType extends Record<string, unknown>>(
  item: SitecoreFieldTypes | ComponentFields
): ReturnType => {
  const itemKeys = Object.keys(item ?? {});
  const uniqueKeys = Array.from(new Set(itemKeys.map((key) => key.replace('Shared', ''))));

  const newObj = uniqueKeys.reduce<Record<string, SitecoreField | unknown>>((acc, key) => {
    const sharedKey = `${key}Shared`;
    const dictionaryKey = `${key}Dictionary`;
    let field = item[key];
    const rawSharedField = item[sharedKey];
    const sharedField = rawSharedField !== undefined ? rawSharedField : item[dictionaryKey];

    const isLocalValuePresent = typeof field !== 'undefined';
    if (isLocalValuePresent) {
      if (typeof field === 'string') {
        acc[key] = { value: field };
        return acc;
      }
      field = field as Field;
      if (isBooleanField(field)) {
        acc[key] = { value: field.value === 'True' };
        return acc;
      }

      if (typeof field?.value === 'string' && !isEmpty(field.value)) {
        acc[key] = field;
        return acc;
      }

      if (isLinkField(field)) {
        acc[key] = { value: field.value };
        return acc;
      }

      if (isMediaItem(field)) {
        acc[key] = field;
        return acc;
      }

      if (isFilledIn(field?.value)) {
        acc[key] = field;
        return acc;
      }

      if (isDictionaryField(field)) {
        const { fields } = field;
        acc[key] = { value: fields?.Phrase?.value || fields?.PhraseShared?.value || '' };
        return acc;
      }

      if (isDroptree(field)) {
        const asItem = field as unknown as Item;
        const fields = mapSharedPropertiesToFields(asItem.fields);
        acc[key] = {
          ...fields,
          id: asItem.id || null,
          name: asItem.name || null,
          ...(asItem.url ? { url: asItem.url } : {}),
        };
        return acc;
      }

      if (isArray(field) && field.length > 0) {
        acc[key] = field.map((item) => {
          if (isMediaItem(item)) {
            return item;
          } else {
            const fields = mapSharedPropertiesToFields(item.fields);
            return {
              ...fields,
              id: item.id || null,
              name: item.name || null,
              ...(item.url ? { url: item.url } : {}),
            };
          }
        });
        return acc;
      }

      if (isJsonValueField(field) && !!field.jsonValue.length) {
        acc[key] = field.jsonValue.map((jsonItem) => {
          const fields = mapSharedPropertiesToFields(jsonItem.fields);
          return { ...fields, id: jsonItem.id };
        });
        return acc;
      }
    }

    const sharedDictionary = getSharedDictionary(
      item as DictionaryFields,
      key as keyof typeof item
    );
    if (typeof sharedField === 'string') {
      console.error(
        `ERROR reading shared field ${sharedKey}. Got only ID instead of value. Probably orphaned reference or indexing issue`
      );
      return acc;
    }

    if (sharedField) {
      if (isItemArray(sharedField)) {
        acc[key] = sharedField.map((item) => {
          if (isMediaItem(item)) {
            return item;
          } else {
            const fields = mapSharedPropertiesToFields(item.fields);
            return {
              ...fields,
              id: item.id || null,
              name: item.name || null,
              ...(item.url ? { url: item.url } : {}),
            };
          }
        });
      } else if (isJsonValueField(sharedField)) {
        acc[key] = sharedField.jsonValue.map((jsonItem) => {
          const fields = mapSharedPropertiesToFields(jsonItem.fields);
          return { ...fields, id: jsonItem.id };
        });
      } else if ('value' in sharedField && isBooleanField(sharedField)) {
        acc[key] = {
          value: sharedField.value === 'True',
        };
      } else if ('value' in sharedField) {
        acc[key] = {
          value: sharedField.value,
        };
      } else if (sharedDictionary && 'value' in sharedDictionary) {
        acc[key] = {
          value: sharedDictionary.value,
        };
      } else if (isDroptree(sharedField)) {
        const asItem = sharedField as unknown as Item;
        const fields = mapSharedPropertiesToFields(asItem.fields);
        acc[key] = {
          ...fields,
          id: asItem.id || null,
          name: asItem.name || null,
          ...(asItem.url ? { url: asItem.url } : {}),
        };
        return acc;
      } else {
        acc[key] = {
          value: '',
        };
      }
    } else {
      acc[key] = field ?? { value: '' };
    }

    return acc;
  }, {});

  const propertiesToPreserve = ['componentName', 'displayName', 'name', 'uid'];

  for (const prop of propertiesToPreserve) {
    if (item && prop in item) {
      newObj[prop] = item[prop];
    }
  }

  return newObj as ReturnType;
};
